import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getJob, updateJob } from '../services/jobService';
import SkillsPicker from '../components/SkillsPicker';
import { validateJobForm, fieldErrorsFromServer, todayInJobTimeZone, maxStartDate, EMPLOYMENT_TYPES, DURATION_UNITS } from '../utils/jobValidation';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATES = [
  { code: 'AL', name: 'Alabama' },      { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },      { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },   { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },      { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },       { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },     { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },         { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },     { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },        { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },{ code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },    { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },     { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },     { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },{ code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },   { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },{ code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },         { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },       { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },        { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },      { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },   { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },    { code: 'WY', name: 'Wyoming' },
];


// ─── Styles ──────────────────────────────────────────────────────────────────

function formatPostedDate(ts) {
  if (!ts) return null;
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

const inputStyle = (hasError = false) => ({
  width: '100%',
  padding: '0.5rem',
  border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
  borderRadius: '4px',
  fontSize: '16px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
});

const errorStyle = { color: '#dc2626', fontSize: '13px', marginTop: '4px' };
const labelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '14px' };
const subLabelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: '400', fontSize: '13px', color: '#6b7280' };
const helpStyle = { fontSize: '12px', color: '#6b7280', marginTop: '4px' };
const fieldGroup = { marginBottom: '1rem' };

// ─── Toggle ──────────────────────────────────────────────────────────────────

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: checked ? '#0f766e' : '#d1d5db',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '22px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

function EditJobPage() {
  const navigate = useNavigate();
  const { jobId } = useParams();

  const [formData, setFormData] = useState({
    title: '',
    companyName: '',
    description: '',
    type: 'Full-time',
    positions: 1,
    salaryMin: '',
    salaryMax: '',
    jobDurationValue: '',
    jobDurationUnit: 'Weeks',
    startDate: '',
    endDate: '',
    state: '',
    city: '',
    country: 'US',
    expiryDays: 60,
    requirements: '',
    benefits: '',
    skills: [],
    i9Required: true,
    status: 'active',
    postedAt: null,
    secondLanguage: '',
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [allCities, setAllCities] = useState([]);
  const [showI9Modal, setShowI9Modal] = useState(false);
  const [positionsFilled, setPositionsFilled] = useState(0);
  const [showCloseJobModal, setShowCloseJobModal] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState(null);
  // As loaded: the start-date window is re-checked only if startDate changes
  // (or a draft is published), so an already-started job stays editable.
  const [original, setOriginal] = useState({ startDate: '', status: '' });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [jobData, citiesSnap] = await Promise.all([
          getJob(jobId),
          getDocs(collection(db, 'cities')),
        ]);

        if (!jobData) {
          setFetchError('Job not found.');
          setFetching(false);
          return;
        }

        const cityList = citiesSnap.docs
          .map(d => ({ cityId: d.id, ...d.data() }))
          .filter(c => c.enabled);

        setAllCities(cityList);

        setFormData({
          title: jobData.title || '',
          companyName: jobData.companyName ?? jobData.company ?? '',
          description: jobData.description || '',
          // Prompt #474: no silent default; a job with no stored type shows an empty select.
          type: jobData.type || '',
          positions: jobData.positions ?? 1,
          salaryMin: jobData.salaryMin != null ? String(jobData.salaryMin) : '',
          salaryMax: jobData.salaryMax != null ? String(jobData.salaryMax) : '',
          jobDurationValue: jobData.jobDurationValue != null ? String(jobData.jobDurationValue) : '',
          jobDurationUnit: jobData.jobDurationUnit || 'Weeks',
          startDate: jobData.startDate || '',
          endDate: jobData.endDate || '',
          state: jobData.state || '',
          city: jobData.city || '',
          country: jobData.country || 'US',
          expiryDays: jobData.expiryDays ?? 60,
          requirements: Array.isArray(jobData.requirements)
            ? jobData.requirements.join('\n')
            : jobData.requirements || '',
          benefits: Array.isArray(jobData.benefits)
            ? jobData.benefits.join('\n')
            : jobData.benefits || '',
          skills: Array.isArray(jobData.skills) ? jobData.skills : [],
          i9Required: jobData.i9Required !== false,
          status: jobData.status || 'active',
          postedAt: jobData.postedAt || null,
          secondLanguage: jobData.secondLanguage || '',
        });
        setPositionsFilled(jobData.positionsFilled ?? 0);
        setOriginal({ startDate: jobData.startDate || '', status: jobData.status || 'active' });
      } catch (err) {
        console.error('EditJobPage: loadData error', err);
        setFetchError('Failed to load job. Please try again.');
      } finally {
        setFetching(false);
      }
    };

    loadData();
  }, [jobId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'state') updated.city = '';
      return updated;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const filteredCities = allCities.filter(c => c.state === formData.state);
  const noEnabledCities = formData.state !== '' && filteredCities.length === 0;

  function buildUpdates(status) {
    return {
      title: formData.title.trim(),
      companyName: formData.companyName,
      description: formData.description.trim(),
      type: formData.type || null,
      positions: Number(formData.positions),
      salaryMin: formData.salaryMin !== '' ? Number(formData.salaryMin) : null,
      salaryMax: formData.salaryMax !== '' ? Number(formData.salaryMax) : null,
      jobDurationValue: formData.jobDurationValue !== '' ? Number(formData.jobDurationValue) : null,
      jobDurationUnit: formData.jobDurationValue !== '' ? formData.jobDurationUnit : null,
      startDate: formData.startDate || null,
      endDate: formData.type !== 'Full-time' ? (formData.endDate || null) : null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country,
      expiryDays: Number(formData.expiryDays),
      requirements: formData.requirements.split('\n').filter(r => r.trim()),
      benefits: formData.benefits.split('\n').filter(b => b.trim()),
      skills: formData.skills,
      i9Required: formData.i9Required,
      secondLanguage: formData.secondLanguage || null,
      status,
    };
  }

  function validate(isDraft) {
    return validateJobForm(formData, {
      isDraft,
      mode: 'edit',
      originalStartDate: original.startDate,
      originalStatus: original.status,
      noEnabledCities,
    });
  }

  const executeUpdate = async (updates) => {
    setLoading(true);
    try {
      await updateJob(jobId, updates);
      navigate('/manage-jobs');
    } catch (err) {
      // Server invalid-argument errors carry { field, code }; show them per field.
      const fieldErrs = fieldErrorsFromServer(err);
      if (Object.keys(fieldErrs).length > 0) {
        setErrors(fieldErrs);
        setSubmitError('Please fix the highlighted fields.');
      } else {
        setSubmitError('Failed to update job. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPositionsGuard = (updates) => {
    const newPositions = Number(formData.positions);
    if (newPositions < positionsFilled) {
      setErrors(prev => ({ ...prev, positions: `Cannot reduce below ${positionsFilled} — ${positionsFilled} worker(s) already hired for this position.` }));
      return false;
    }
    if (newPositions === positionsFilled && formData.status !== 'closed') {
      setPendingUpdates(updates);
      setShowCloseJobModal(true);
      return false;
    }
    return true;
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errs = validate(false);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const updates = buildUpdates(formData.status);
    if (!checkPositionsGuard(updates)) return;
    await executeUpdate(updates);
  };

  const handleSaveDraft = async () => {
    setSubmitError('');
    const errs = validate(true);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const updates = buildUpdates('draft');
    if (!checkPositionsGuard(updates)) return;
    await executeUpdate(updates);
  };

  const handlePostJob = async () => {
    setSubmitError('');
    const errs = validate(false);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const updates = buildUpdates('active');
    if (!checkPositionsGuard(updates)) return;
    await executeUpdate(updates);
  };

  if (fetching) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem' }}>
        <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading job...</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem' }}>
        <div style={{ backgroundColor: '#F8FAFC', padding: '2rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <p style={{ color: '#991b1b', fontSize: '16px', marginBottom: '1rem' }}>{fetchError}</p>
          <button
            onClick={() => navigate('/manage-jobs')}
            style={{ backgroundColor: '#6b7280', color: 'white', padding: '0.5rem 1rem', borderRadius: '4px', border: 'none', cursor: 'pointer', fontWeight: '500' }}
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  const isDraft = formData.status === 'draft';
  const isFullTime = formData.type === 'Full-time';
  const today = todayInJobTimeZone();
  const startDateMax = maxStartDate(today);
  const startDateChanged = formData.startDate !== original.startDate || original.status === 'draft';
  const endDateMin = formData.startDate || today;

  return (
    <>
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.25rem' }}>
        <span
          onClick={() => navigate('/manage-jobs')}
          style={{ cursor: 'pointer', color: '#6b7280', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}
          onMouseEnter={e => e.currentTarget.style.color = '#E8590C'}
          onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          Manage Jobs
        </span>
        <span style={{ color: '#d1d5db', fontSize: '13px' }}>/</span>
        <span style={{ color: '#374151', fontSize: '13px', fontWeight: '500' }}>Edit Job</span>
      </div>

      <div style={{ backgroundColor: '#F8FAFC', padding: '2rem', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>

        {submitError && (
          <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
            {submitError}
          </div>
        )}

        {/* noValidate (QA 2.5.18, Prompt #496): validateJobForm covers every native
            check (drafts are format-only), so the browser no longer blocks a
            submit with its own message; inputs keep min/max for the picker. */}
        <form noValidate onSubmit={isDraft ? (e) => { e.preventDefault(); handleSaveDraft(); } : handleSaveChanges}>

          {/* 1 — Job Title */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Job Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Construction Worker"
              style={inputStyle(!!errors.title)}
            />
            {errors.title && <div style={errorStyle}>{errors.title}</div>}
            {(formData.postedAt || formData.status === 'draft') && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {formData.postedAt ? `Posted: ${formatPostedDate(formData.postedAt)}` : 'Posted: TBA'}
              </div>
            )}
          </div>

          {/* 2 — Company Name (read-only from job doc) */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Company Name</label>
            <input
              type="text"
              value={formData.companyName}
              readOnly
              style={{ ...inputStyle(false), backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
            />
            <div style={helpStyle}>From your company profile</div>
          </div>

          {/* 3 — Job Description */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Job Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Describe the role, responsibilities, and what you're looking for…"
              style={{ ...inputStyle(!!errors.description), resize: 'vertical' }}
            />
            {errors.description && <div style={errorStyle}>{errors.description}</div>}
          </div>

          {/* 4 — Second Language */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Second Language (optional)</label>
            <select
              name="secondLanguage"
              value={formData.secondLanguage}
              onChange={handleChange}
              style={inputStyle(false)}
            >
              <option value="">None</option>
              <option value="es">Español (Spanish)</option>
              <option value="zh">中文简体 (Chinese Simplified)</option>
              <option value="zh-TW">中文繁體 (Chinese Traditional)</option>
              <option value="ko">한국어 (Korean)</option>
              <option value="ja">日本語 (Japanese)</option>
            </select>
            <div style={helpStyle}>Translate this job posting into a second language for workers</div>
          </div>

          {/* 5 — Employment Type */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Employment Type *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              style={inputStyle(!!errors.type)}
            >
              <option value="">Select…</option>
              {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.type && <div style={errorStyle}>{errors.type}</div>}
          </div>

          {/* 6 — Number of Positions */}
          <div style={fieldGroup}>
            <label style={labelStyle}>How many workers do you need?</label>
            <input
              type="number"
              name="positions"
              value={formData.positions}
              onChange={handleChange}
              min="1"
              max="999"
              step="1"
              style={{ ...inputStyle(!!errors.positions), width: '120px' }}
            />
            {errors.positions && <div style={errorStyle}>{errors.positions}</div>}
            {positionsFilled > 0 && !errors.positions && (
              <div style={helpStyle}>
                {positionsFilled} of {formData.positions} position{Number(formData.positions) !== 1 ? 's' : ''} filled
              </div>
            )}
            {formData.status === 'closed' && Number(formData.positions) > positionsFilled && (
              <div style={{ fontSize: '12px', color: '#0f766e', marginTop: '4px' }}>
                Increasing positions will reopen this job posting.
              </div>
            )}
          </div>

          {/* 6 — Pay Range */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Pay Range <span style={{ fontWeight: '400', color: '#6b7280' }}>(optional)</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={subLabelStyle}>Min Pay</label>
                <input
                  type="number"
                  name="salaryMin"
                  value={formData.salaryMin}
                  onChange={handleChange}
                  placeholder="e.g. 15"
                  min="0.01"
                  step="0.01"
                  style={inputStyle(!!errors.salaryMin)}
                />
                {errors.salaryMin && <div style={errorStyle}>{errors.salaryMin}</div>}
              </div>
              <div>
                <label style={subLabelStyle}>Max Pay</label>
                <input
                  type="number"
                  name="salaryMax"
                  value={formData.salaryMax}
                  onChange={handleChange}
                  placeholder="e.g. 25"
                  min="0.01"
                  step="0.01"
                  style={inputStyle(!!errors.salaryMax)}
                />
                {errors.salaryMax && <div style={errorStyle}>{errors.salaryMax}</div>}
              </div>
            </div>
            <div style={helpStyle}>Pay period and details are agreed between employer and worker</div>
          </div>

          {/* 7 — Job Duration */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Estimated Duration <span style={{ fontWeight: '400', color: '#6b7280' }}>(optional)</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '1rem' }}>
              <input
                type="number"
                name="jobDurationValue"
                value={formData.jobDurationValue}
                onChange={handleChange}
                placeholder="e.g. 3"
                min="1"
                step="1"
                style={inputStyle(!!errors.jobDurationValue)}
              />
              <select
                name="jobDurationUnit"
                value={formData.jobDurationUnit}
                onChange={handleChange}
                style={inputStyle(false)}
              >
                {DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            {errors.jobDurationValue && <div style={errorStyle}>{errors.jobDurationValue}</div>}
          </div>

          {/* 8 — Start Date */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              min={startDateChanged ? today : undefined}
              max={startDateChanged ? startDateMax : undefined}
              style={inputStyle(!!errors.startDate)}
            />
            {errors.startDate && <div style={errorStyle}>{errors.startDate}</div>}
          </div>

          {/* 9 — End Date (hidden for Full-time) */}
          <div style={fieldGroup}>
            <label style={labelStyle}>End Date {!isFullTime && '*'}</label>
            {isFullTime ? (
              <div style={{ fontSize: '14px', color: '#6b7280', padding: '0.4rem 0', fontStyle: 'italic' }}>
                Ongoing (Full-time position)
              </div>
            ) : (
              <>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={endDateMin}
                  style={inputStyle(!!errors.endDate)}
                />
                {errors.endDate && <div style={errorStyle}>{errors.endDate}</div>}
              </>
            )}
          </div>

          {/* 10 — Location */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Location *</label>

            {/* State */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={subLabelStyle}>State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                style={inputStyle(!!errors.state)}
              >
                <option value="">Select state…</option>
                {STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
              {errors.state && <div style={errorStyle}>{errors.state}</div>}
            </div>

            {/* City — preserve existing value even if not in enabled list */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={subLabelStyle}>City</label>
              {noEnabledCities && !formData.city ? (
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  padding: '0.6rem 0.75rem',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}>
                  No cities currently available in this state.{' '}
                  Contact <a href="mailto:support@workrmatch.com" style={{ color: '#0f766e' }}>support@workrmatch.com</a>{' '}
                  to request your city.
                </div>
              ) : (
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!formData.state}
                  style={{
                    ...inputStyle(!!errors.city),
                    opacity: !formData.state ? 0.6 : 1,
                    cursor: !formData.state ? 'not-allowed' : 'default',
                    backgroundColor: !formData.state ? '#f3f4f6' : 'white',
                  }}
                >
                  <option value="">
                    {formData.state ? 'Select city…' : 'Select a state first'}
                  </option>
                  {filteredCities.map(c => (
                    <option key={c.cityId} value={c.name}>{c.name}</option>
                  ))}
                  {/* Preserve existing city if it's not in the enabled list */}
                  {formData.city && !filteredCities.find(c => c.name === formData.city) && (
                    <option value={formData.city}>{formData.city}</option>
                  )}
                </select>
              )}
              {errors.city && <div style={errorStyle}>{errors.city}</div>}
            </div>

            {/* Country */}
            <div>
              <label style={subLabelStyle}>Country</label>
              <input
                type="text"
                value={formData.country === 'US' ? 'United States' : formData.country}
                readOnly
                style={{ ...inputStyle(false), backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
              />
            </div>
          </div>

          {/* 12 — Job Expires After */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Job Expires After</label>
            <select
              name="expiryDays"
              value={formData.expiryDays}
              onChange={handleChange}
              style={inputStyle(false)}
            >
              <option value={30}>30 days</option>
              <option value={60}>60 days (recommended)</option>
              <option value={90}>90 days</option>
            </select>
          </div>

          {/* 13 — Requirements */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Requirements * <span style={{ fontWeight: '400', color: '#6b7280' }}>(one per line)</span></label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              rows="4"
              placeholder={'Experience in construction\nValid driver\'s license\nPhysical fitness'}
              style={{ ...inputStyle(!!errors.requirements), resize: 'vertical' }}
            />
            {errors.requirements && <div style={errorStyle}>{errors.requirements}</div>}
          </div>

          {/* 13b — Skills */}
          <div style={fieldGroup}>
            <SkillsPicker
              value={formData.skills}
              onChange={(skills) => setFormData(prev => ({ ...prev, skills }))}
            />
          </div>

          {/* 14 — Benefits */}
          <div style={{ ...fieldGroup, marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Benefits <span style={{ fontWeight: '400', color: '#6b7280' }}>(one per line)</span></label>
            <textarea
              name="benefits"
              value={formData.benefits}
              onChange={handleChange}
              rows="4"
              placeholder={'Health insurance\nPaid time off\nTraining opportunities'}
              style={{ ...inputStyle(!!errors.benefits), resize: 'vertical' }}
            />
            {errors.benefits && <div style={errorStyle}>{errors.benefits}</div>}
          </div>

          {/* 15 — I-9 Verification */}
          <div style={{
            ...fieldGroup,
            padding: '1rem',
            backgroundColor: formData.i9Required ? '#f0fdfa' : '#fffbeb',
            border: `1px solid ${formData.i9Required ? '#99f6e4' : '#f59e0b'}`,
            borderRadius: '6px',
            marginBottom: '1.25rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Toggle
                checked={formData.i9Required}
                onChange={val => {
                  if (!val) {
                    setShowI9Modal(true);
                  } else {
                    setFormData(prev => ({ ...prev, i9Required: true }));
                  }
                }}
              />
              <label style={{ ...labelStyle, margin: 0, cursor: 'pointer', fontSize: '14px' }}>
                Require I-9 Verification
              </label>
            </div>
            <div style={{ ...helpStyle, marginTop: '8px', color: formData.i9Required ? '#0f766e' : '#92400e' }}>
              {formData.i9Required
                ? 'Workers will complete I-9 verification through WorkBright after offer acceptance'
                : 'I-9 verification disabled. You have accepted full legal responsibility for employment eligibility compliance.'}
            </div>
          </div>

          {/* 16 — Status (only for non-draft jobs) */}
          {!isDraft && (
            <div style={{ ...fieldGroup, marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={inputStyle(false)}
              >
                {!formData.postedAt && <option value="draft">Draft</option>}
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          )}

          {/* Buttons */}
          {isDraft ? (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1,
                }}
              >
                {loading ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={handlePostJob}
                style={{
                  flex: 2,
                  backgroundColor: '#E8590C',
                  color: 'white',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1,
                }}
              >
                {loading ? 'Posting...' : 'Post Job'}
              </button>
            </div>
          ) : (
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: '#E8590C',
                color: 'white',
                padding: '0.75rem',
                borderRadius: '6px',
                border: 'none',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          )}

        </form>
      </div>
    </div>

    {showCloseJobModal && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #E8590C',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '480px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#9a3412', marginBottom: '12px' }}>
            Close This Job?
          </div>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px', lineHeight: '1.5' }}>
            Reducing positions to {formData.positions} matches the number of workers already hired. This will:
          </p>
          <ul style={{ margin: '0 0 20px 0', padding: '0 0 0 20px' }}>
            <li style={{ marginBottom: '8px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Close the job posting
            </li>
            <li style={{ marginBottom: '8px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Notify all workers currently in conversation
            </li>
            <li style={{ color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              Reject all pending applications
            </li>
          </ul>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
            This action cannot be undone from here.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={async () => {
                setShowCloseJobModal(false);
                await executeUpdate(pendingUpdates);
              }}
              style={{
                backgroundColor: '#E8590C',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Close Job
            </button>
            <button
              onClick={() => { setShowCloseJobModal(false); setPendingUpdates(null); }}
              style={{
                backgroundColor: 'white',
                color: '#374151',
                padding: '10px 16px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {showI9Modal && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '480px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#92400e', marginBottom: '16px' }}>
            Disable I-9 Verification?
          </div>
          <ul style={{ margin: '0 0 20px 0', padding: '0 0 0 20px' }}>
            <li style={{ marginBottom: '10px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              You are solely responsible for verifying the employment eligibility of all workers hired through this posting, as required by federal law.
            </li>
            <li style={{ marginBottom: '10px', color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              WorkrMatch accepts no liability for your compliance with employment eligibility verification requirements, including but not limited to the Immigration Reform and Control Act (IRCA).
            </li>
            <li style={{ color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
              You may be subject to civil and criminal penalties for failure to properly verify employment eligibility.
            </li>
          </ul>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={() => { setFormData(prev => ({ ...prev, i9Required: false })); setShowI9Modal(false); }}
              style={{
                backgroundColor: '#f59e0b',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              I Accept Full Responsibility
            </button>
            <button
              onClick={() => { setFormData(prev => ({ ...prev, i9Required: true })); setShowI9Modal(false); }}
              style={{
                backgroundColor: 'white',
                color: '#374151',
                padding: '10px 16px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Cancel — Keep I-9 Enabled
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default EditJobPage;
